"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Catalog is the source of truth",
    rule: "Analytics Dashboard lists 28 named analytics in seven groups. There is no Sales / Purchase / Inventory / Customer KPI dashboard on this screen",
  },
  {
    topic: "Not Reports",
    rule: "Ledgers, aging buckets, and Gross Profit live under Reports. Analytics is rankings, pending orders, pipeline counts, stock by location, collection/payments lists, and production rejection",
  },
  {
    topic: "Apply Filters loads the data",
    rule: "Changing a dropdown does not refresh the table. Click Apply Filters. Inventory Analytics has no filter bar — it loads on open",
  },
  {
    topic: "Export is rare",
    rule: "Only Item Rejection Analytics (PDF and XLSX) and Item Process Analytics (XLSX) have Export Report. The others are on-screen only",
  },
  {
    topic: "Permissions",
    rule: "Each analytics has its own view permission. A group with no allowed children is hidden. Created By is always System Generated. Access is Settings → Roles, not a fixed Admin / Manager / Sales / Accountant map",
  },
  {
    topic: "Branch follows Division",
    rule: "Where both exist, pick Division first. Branch is disabled until Division is set",
  },
];

const SALES_ROWS = [
  {
    name: "Sales Orders Analytics",
    what: "Open sales orders with stock and value. Header: Sales Orders, Unique Items, Ordered Qty, Invoiced Qty, Total Amount. Tabs: Sales Orders Wise and Item Group Wise. Click an item tag for the item drawer",
    filters: "Date, Division → Branch, Item, Customer",
  },
  {
    name: "Customer Wise Pending Order Analytics",
    what: "Pending sales orders grouped by customer: Pending Sales Orders, Oldest Order Date, Total Quantity, and value",
    filters: "Date, Division → Branch, Item, Customer",
  },
  {
    name: "Item Wise Sales Orders Analytics",
    what: "Pending sales orders by item: Item#, Item Name, linked Sales Orders",
    filters: "Date, Division → Branch, Item, Customer, Item Type, Item Group",
  },
  {
    name: "New vs Old Customer Report",
    what: "Customer rows with Total Orders, invoices, delivery challans, payments. Click a count → that customer's Sales Orders, Delivery Challans, Invoices, or Payments list",
    filters: "Date, Division → Branch, Customer",
  },
  {
    name: "Top Customers of Sales",
    what: "Customers ranked by Quote / Sales Order / Delivery Challan / Invoice qty and value. Sortable columns",
    filters: "Date, Division → Branch, Item, Item Group",
  },
  {
    name: "Top Items of Sales",
    what: "Items ranked the same way (quote through invoice qty and value)",
    filters: "Date, Division → Branch, Customer, Item Group",
  },
  {
    name: "Customers With Single Invoice",
    what: "Customers who have exactly one invoice: name, company, phone, Invoice ID (click to open), date",
    filters: "Division → Branch (no date picker)",
  },
  {
    name: "General Sales Analytics",
    what: "Saved named reports you create. List: Report Name, Basis, Created By, Created At. Click a row for the detail",
    filters: "Create: Report Name, Report Basis (Item Group or Item), then those groups or items. Detail: date range → Apply Filters. Documents can be Quote, Sales Order, Delivery Challan, Invoice",
  },
  {
    name: "RM Analytics",
    what: "Raw-material shortage vs sales orders: Item#, Item Name, Required Qty, Available Qty, Shortage Qty",
    filters: "Status (sales-order status), Division → Branch, Item, Customer (no date picker)",
  },
  {
    name: "Item Wise RM Analytics",
    what: "Raw-material need by item against sales orders",
    filters: "Division → Branch, Item, Customer",
  },
  {
    name: "Sales Order Wise RM Analytics",
    what: "Raw-material need by sales order",
    filters: "Status, Division → Branch, Item, Customer",
  },
];

const sections = [
  {
    heading: "What Analytics is",
    subsections: [
      { heading: "1. Open the catalog" },
      { heading: "2. Filters, Apply, and date presets" },
      { heading: "3. Export Report" },
    ],
  },
  {
    heading: "The analytics",
    subsections: [
      { heading: "1. Item" },
      { heading: "2. Inventory" },
      { heading: "3. Sales" },
      { heading: "4. Purchase" },
      { heading: "5. Collection" },
      { heading: "6. Production" },
      { heading: "7. Payments" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function AnalyticsOverviewPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Analytics Dashboard
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who needs rankings, pending orders, pipeline counts,
            stock by location, collection or payments lists, or production
            rejection — not ledger statements
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Sidebar <B>Analytics</B> opens a catalog of 28 named analytics in
            seven groups. Click a name. Most screens use{" "}
            <B>Apply Filters</B> after you set a date range and Division →
            Branch. This is not Reports, and it is not a single sales KPI
            dashboard.
          </p>
          <Flow
            steps={[
              "Sidebar",
              "Analytics",
              "click a name",
              "set filters",
              "Apply Filters",
            ]}
          />
          <Callout type="note">
            Groups: Item, Inventory, Sales, Purchase, Collection, Production,
            Payments. Info (i) next to a name shows the in-app tooltip. Profit
            & Loss, Balance Sheet, and General Ledger are not here.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What Analytics is
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Open the catalog
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title is <B>Analytics Dashboard</B>. Columns are <B>Name</B>,{" "}
            <B>Type</B>, <B>Created By</B> (always System Generated). Grey
            header rows are groups with a count of the reports you can view.
          </p>
          <Steps
            items={[
              <>
                Open <B>Analytics</B> in the left sidebar.
              </>,
              <>
                Find the group, then click the <B>Name</B> (or the info icon
                for the tooltip).
              </>,
              <>
                Use the back arrow on any analytics screen to return to this
                catalog.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Item",
                desc: "Item Analytics, Group Item",
              },
              { term: "Inventory", desc: "Inventory Analytics" },
              {
                term: "Sales",
                desc: "11 analytics — sales orders, pending orders, top customers/items, new vs old, single-invoice customers, general sales, and three RM views",
              },
              {
                term: "Purchase",
                desc: "Top Vendors By Amount, Top Vendors By Purchase, Top Item Of Purchase",
              },
              {
                term: "Collection",
                desc: "Top Customer By Amount, Upcoming Receivables Invoice, Recently Received Payments, Overdue Invoices",
              },
              {
                term: "Production",
                desc: "Rejection Analytics, Item Rejection Analytics, Item Process Analytics",
              },
              {
                term: "Payments",
                desc: "Top Vendors By Amount, Upcoming Payables Bills, Recently Payments Made, Overdue Bills",
              },
            ]}
          />
          <Callout type="tip">
            A missing name means your role does not have view on that
            analytics. If no child in a group is allowed, the group is hidden.
          </Callout>
          <Screenshot caption="Analytics Dashboard — grouped table of Name, Type, Created By, with info tooltips" />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Filters, Apply, and date presets
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Most analytics show <B>View Filters</B> / <B>Hide Filters</B>. Set
            the filters, then run.
          </p>
          <Steps
            items={[
              <>
                If the bar is hidden, click <B>View Filters</B>.
              </>,
              <>
                Where both exist: <B>Division</B> → <B>Branch</B>. Branch stays
                disabled until Division is set.
              </>,
              <>
                Set the date range when the picker is there, then click{" "}
                <B>Apply Filters</B>. Active filters show as tags.{" "}
                <B>Reset All</B> clears them.
              </>,
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-2">
            Date presets on a range picker:
          </p>
          <Flow
            steps={[
              "Today",
              "Last 7 Days",
              "Last 15 Days",
              "Last Month",
              "Last 3 Months",
              "Custom Range",
              "Specific Date",
            ]}
          />
          <Callout type="warning">
            Inventory Analytics has no date range and no Apply Filters — it
            loads on open. Customers With Single Invoice and the RM analytics
            have no date picker. General Sales Analytics is a list of saved
            reports until you open one.
          </Callout>

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Export Report
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Almost none of these screens export. Only two do:
          </p>
          <Definitions
            items={[
              {
                term: "Item Rejection Analytics",
                desc: "Export Report → PDF or XLSX → filename in the modal",
              },
              {
                term: "Item Process Analytics",
                desc: "Export Report → XLSX only",
              },
            ]}
          />
          <Callout type="note">
            Rankings, pending orders, collection, payments, Group Item, and
            Inventory Analytics have no Export Report.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            The analytics
          </h2>

          <h3
            id="section-1-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Item
          </h3>
          <p className="text-[16px] geist-medium text-[#111827] mb-2">
            Item Analytics
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pipeline counts per item — where quantity sits across documents.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Item Analytics",
              "date / Division / Branch / Item",
              "Apply Filters",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Item Name, Quotation, Sales Order, Delivery Challan, Invoice, Purchase Order, Bill, GRPO, Production Order (qty with a role tag)",
              },
            ]}
          />

          <p className="text-[16px] geist-medium text-[#111827] mb-2 mt-6">
            Group Item
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            You build named groups of item groups, then see Sales Order qty /
            amount vs Sales qty / amount. Click a qty or amount cell to expand
            the items in that group.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Group Item",
              "New Group",
              "pick item groups",
              "Save",
              "date / Division / Branch",
              "Apply Filters",
              "click a cell to expand",
            ]}
          />
          <Steps
            items={[
              <>
                Click <B>New Group</B>, name the block, pick Item Groups, then{" "}
                <B>Save</B>.
              </>,
              <>
                Set date, Division → Branch, click <B>Apply Filters</B>.
              </>,
              <>
                Click <B>Sales Order</B> / <B>Sales Order Amount</B> to expand
                orders, or <B>Sales</B> / <B>Sales Amount</B> for invoices.
              </>,
            ]}
          />
          <Screenshot caption="Item Analytics pipeline columns; Group Item with New Group, Save, and expandable Sales Order / Sales cells" />

          <h3
            id="section-1-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Inventory
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            On-hand stock by location. Title <B>Inventory</B>. Header cards:{" "}
            <B>Total Items</B>, <B>Total Quantity</B>, <B>Total Value</B>. No
            date range, no Apply Filters, no export.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Inventory Analytics",
              "expand Division",
              "Branch",
              "Warehouse",
              "item group",
            ]}
          />
          <Steps
            items={[
              <>
                Open it. Totals load immediately.
              </>,
              <>
                Click a division card → branches. Click a branch → warehouses.
                Click a warehouse → item groups. Item rows: Item, Quantity,
                Price per item, Value.
              </>,
            ]}
          />
          <Callout type="note">
            This is a stock position by location. Period In/Out is Reports →
            Item Ledger. On-hand list by item is View Inventory.
          </Callout>
          <Screenshot caption="Inventory Analytics — Total Items / Quantity / Value, expand Division → Branch → Warehouse" />

          <h3
            id="section-1-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Sales
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Eleven analytics. Shared path for most of them:
          </p>
          <Flow
            steps={[
              "Analytics",
              "Sales group",
              "click a name",
              "filters",
              "Apply Filters",
            ]}
          />
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Name
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    What it gives you
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Filters
                  </th>
                </tr>
              </thead>
              <tbody>
                {SALES_ROWS.map((row) => (
                  <tr
                    key={row.name}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.name}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151] align-top">
                      {row.what}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151] align-top">
                      {row.filters}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[16px] geist-medium text-[#111827] mb-2">
            General Sales Analytics — extra steps
          </p>
          <Steps
            items={[
              <>
                Click <B>Create General Sales Analytics</B>.
              </>,
              <>
                Enter <B>Report Name</B>. Set <B>Report Basis</B> to Item Group
                or Item, then pick those groups or items. Save.
              </>,
              <>
                Click the saved row. On the detail, set a date range →{" "}
                <B>Apply Filters</B>.
              </>,
            ]}
          />
          <p className="text-[16px] geist-medium text-[#111827] mb-2 mt-4">
            New vs Old Customer Report — extra steps
          </p>
          <Flow
            steps={[
              "customer row",
              "click Total Orders / invoices / challans / payments",
              "that module's list for the customer",
            ]}
          />
          <Screenshot caption="Sales analytics — Sales Orders tabs, Top Customers ranking, or Create General Sales Analytics" />

          <h3
            id="section-1-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Purchase
          </h3>
          <Flow
            steps={[
              "Analytics",
              "Purchase",
              "click a name",
              "date / Division / Branch",
              "Apply Filters",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Top Vendors By Amount",
                desc: "Highest-value purchase orders: Order#, Vendor Name, Order Date, Total Amount",
              },
              {
                term: "Top Vendors By Purchase",
                desc: "Vendors ranked by purchase value",
              },
              {
                term: "Top Item Of Purchase",
                desc: "Items ranked by purchase value",
              },
            ]}
          />
          <Screenshot caption="Purchase analytics — vendor or item ranking after Apply Filters" />

          <h3
            id="section-1-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Collection
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Customer money in. Click Invoice# or Payment# to open the document.
            No export.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Collection",
              "click a name",
              "date / Division / Branch",
              "Apply Filters",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Top Customer By Amount",
                desc: "Customer Name, Invoice Count, Total Balance",
              },
              {
                term: "Upcoming Receivables Invoice",
                desc: "Invoice#, Customer Name, Due Date, Amount — invoices coming due in the date range",
              },
              {
                term: "Recently Received Payments",
                desc: "Payment#, Customer Name, Payment Mode, Date, amount",
              },
              {
                term: "Overdue Invoices",
                desc: "Invoice#, Customer Name, Due Date, Amount — unpaid past due",
              },
            ]}
          />
          <Callout type="note">
            Bucketed aging (Days / Weeks / Months, Number of Columns) is
            Reports → Customer Aging and Receivables / Payables, not this
            Collection list.
          </Callout>
          <Screenshot caption="Collection — overdue or upcoming invoices after date and Division / Branch" />

          <h3
            id="section-1-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Production
          </h3>
          <p className="text-[16px] geist-medium text-[#111827] mb-2">
            Rejection Analytics
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title <B>Production Rejection Analytics</B>. KPI cards: Rejection
            Rate, Yield Rate, Total Rejected, Total Orders, RM Rejected. Trend
            chart of planned vs submitted. Tables by item, raw material,
            process step, and branch. Default date preset is Last 3 Months.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Rejection Analytics",
              "date / Production Method / Group Trend By",
              "Apply Filters",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Production Method",
                desc: "All, BOM Based, or Process Route. Process tables show when Process Route applies; RM tables when BOM Based applies",
              },
              {
                term: "Group Trend By",
                desc: "Daily, Weekly, or Monthly",
              },
            ]}
          />

          <p className="text-[16px] geist-medium text-[#111827] mb-2 mt-4">
            Item Rejection Analytics
          </p>
          <Flow
            steps={[
              "Analytics",
              "Item Rejection Analytics",
              "date / Item / Item Type",
              "Apply Filters",
              "Export Report (PDF or XLSX)",
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Item#, Item Name, Production Orders. Click an item for the
            production-order breakdown.
          </p>

          <p className="text-[16px] geist-medium text-[#111827] mb-2 mt-4">
            Item Process Analytics
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Quantity moved between production process steps, per item.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Item Process Analytics",
              "date / Item / Process",
              "Apply Filters",
              "Export Report (XLSX)",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Date, Job No, Product, From, To, Send Qty, Send By, Recv Qty, Recv By",
              },
            ]}
          />
          <Screenshot caption="Production Rejection Analytics KPIs and trend chart; Item Process with From / To qty" />

          <h3
            id="section-1-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Payments
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Vendor money out. Same shape as Collection, for bills and payments
            made. No export.
          </p>
          <Flow
            steps={[
              "Analytics",
              "Payments",
              "click a name",
              "date / Division / Branch",
              "Apply Filters",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Top Vendors By Amount",
                desc: "Vendor Name, Bill Count, Total Balance",
              },
              {
                term: "Upcoming Payables Bills",
                desc: "Bill#, Vendor Name, Due Date, amount — bills coming due in the date range",
              },
              {
                term: "Recently Payments Made",
                desc: "Payment#, vendor, mode, date, amount",
              },
              {
                term: "Overdue Bills",
                desc: "Bill#, Vendor Name, Due Date, amount — unpaid past due",
              },
            ]}
          />
          <Callout type="note">
            Vendor aging buckets are Reports → Vendor Aging, not this Payments
            list.
          </Callout>
          <Screenshot caption="Payments — overdue or upcoming bills after date and Division / Branch" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-2"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/reports/reports-overview"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Reports <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Ledgers, aging buckets, and Gross Profit — not this catalog
            </p>
          </Link>
          <Link
            href="/documentation/books/inventory/view-inventory"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              View Inventory <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              On-hand list by item. Analytics → Inventory is by location
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Items and groups that Item / Group Item analytics read
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
